import { IconListTree } from '@tabler/icons-react';

import { useCallback, useEffect, useRef } from 'react';

import { Group, ScrollArea, Skeleton, Stack, TableOfContents, Text } from '@mantine/core';

export function DocumentOutlineSkeleton() {
    return (
        <Stack p="md" gap="sm">
            <Skeleton height={14} width="60%" mb="md" />
            <Stack gap="xs">
                <Skeleton height={10} width="80%" />
                <Skeleton height={10} width="50%" ml="sm" />
                <Skeleton height={10} width="65%" ml="sm" />
                <Skeleton height={10} width="70%" />
                <Skeleton height={10} width="45%" ml="sm" />
            </Stack>
        </Stack>
    );
}

interface DocumentOutlinePanelProps {
    scrollAreaViewport: HTMLDivElement | null;
    reinitializeTrigger: number;
}

export function DocumentOutlinePanel({
    scrollAreaViewport,
    reinitializeTrigger
}: DocumentOutlinePanelProps) {
    const noop = () => undefined;
    const reinitializeRef = useRef<() => void>(noop);
    const activeRef = useCallback((node: HTMLElement | null) => {
        node?.scrollIntoView({ block: 'nearest' });
    }, []);

    useEffect(() => {
        reinitializeRef.current();
    }, [reinitializeTrigger]);

    useEffect(() => {
        if (!scrollAreaViewport) return;

        const handleScroll = () => reinitializeRef.current();
        scrollAreaViewport.addEventListener('scroll', handleScroll, {
            passive: true
        });

        return () => {
            scrollAreaViewport.removeEventListener('scroll', handleScroll);
        };
    }, [scrollAreaViewport]);

    if (!scrollAreaViewport) {
        return <DocumentOutlineSkeleton />;
    }

    return (
        <Stack p="md" gap="sm" h="100%">
            <Group gap="xs" pb="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                <IconListTree size={14} stroke={1.5} color="var(--mantine-color-dimmed)" />
                <Text
                    fw={700}
                    size="xs"
                    c="dimmed"
                    style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
                >
                    Document Outline
                </Text>
            </Group>
            <ScrollArea flex={1} offsetScrollbars>
                <TableOfContents
                    variant="light"
                    color="brand"
                    size="sm"
                    reinitializeRef={reinitializeRef}
                    scrollSpyOptions={{
                        selector: [1, 2, 3, 4, 5, 6]
                            .map(
                                (n) =>
                                    `.mantine-ScrollArea-viewport h${String(n)}`
                            )
                            .join(', ')
                    }}
                    getControlProps={({ data, active }) => ({
                        children: data.value,
                        'data-active': active || undefined,
                        ref: active ? activeRef : undefined,
                        onClick: () =>
                            data.getNode().scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                            })
                    })}
                />
            </ScrollArea>
        </Stack>
    );
}
